/**
 * Invented data of a wine bar with kitchen (Osteria Pit, persona P1) for measurements and demos:
 * cellar, bar and kitchen, about 250 articles, a year of bookings (≈ 100 000 by default),
 * monthly inventories. Deterministic for a seed.
 */
import type { EntityChange } from "./entities.ts";
import { uuidv7 } from "./ids.ts";
import type { Booking } from "./model.ts";

export interface DemoData {
  entities: EntityChange[];
  bookings: Booking[];
}

export interface DemoOptions {
  seed?: number;
  /** First day (UTC, start of day). */
  start: number;
  days: number;
  /** Bookings per day on average (≈ 275 for 100 000 a year). */
  perDay?: number;
}

const DAY = 86_400_000;
/** A wine bottle in ml (every drink counts in ml, docs/17-getraenke.md). */
const BOTTLE = 750;

function random(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A valid EAN-13 with check digit, from a 12-digit stem. */
export function ean13(stem: string): string {
  const digits = stem.padStart(12, "0").slice(0, 12);
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(digits[i]) * (i % 2 === 0 ? 1 : 3);
  return digits + ((10 - (sum % 10)) % 10);
}

const WINES = ["Merlot Veneto", "Riesling trocken", "Primitivo di Manduria", "Grüner Veltliner", "Chianti Classico", "Sauvignon Blanc", "Spätburgunder", "Montepulciano d'Abruzzo", "Pinot Grigio", "Rioja Crianza", "Lugana", "Barbera d'Asti", "Weißburgunder", "Nero d'Avola", "Prosecco DOC"];
const SPIRITS = ["Grappa", "Gin", "Campari", "Aperol", "Rum", "Whisky", "Amaro", "Limoncello", "Wodka", "Vermouth"];
const FOODS = ["Mehl Type 00", "Olivenöl", "Tomaten passiert", "Parmesan", "Mozzarella", "Spaghetti", "Risottoreis", "Butter", "Sahne", "Eier", "Zucker", "Salz", "Kaffeebohnen", "Milch", "Zwiebeln", "Knoblauch", "Basilikum", "Rinderfilet", "Lachs", "Zitronen"];

interface Item {
  id: string;
  lager: string;
  location: string;
  lot: boolean;
  /** Typical amount per issue. */
  step: number;
  /** Delivery amount when low. */
  refill: number;
  /** Cents per base unit. */
  price: number;
  stock: number;
  /** Relative popularity. */
  weight: number;
  /** Wine that is also sold at the bar (moved there first). */
  bar?: string;
}

export function demoData(opts: DemoOptions): DemoData {
  const rnd = random(opts.seed ?? 42);
  const pick = <T>(list: T[]) => list[Math.floor(rnd() * list.length)]!;
  const perDay = opts.perDay ?? 275;
  const entities: EntityChange[] = [];
  const bookings: Booking[] = [];
  let at0 = opts.start - DAY;
  const change = (type: EntityChange["type"], id: string, fields: Record<string, unknown>) => {
    at0 += 1;
    entities.push({ op: `demo-${type}-${id}`, type, id, at: at0, fields });
  };

  change("lager", "keller", { name: "Weinkeller", profile: "wein", countMode: "article" });
  change("lager", "bar", { name: "Bar", profile: "wein", countMode: "article" });
  change("lager", "kueche", { name: "Küche", profile: "lebensmittel", countMode: "article" });
  const cellar = ["A", "B", "C", "D", "E", "F"].map((r) => `keller/regal-${r.toLowerCase()}`);
  for (const loc of cellar) change("location", loc, { lager: "keller", name: `Regal ${loc.slice(-1).toUpperCase()}` });
  change("location", "bar/theke", { lager: "bar", name: "Theke" });
  change("location", "bar/kuehlschrank", { lager: "bar", name: "Kühlschrank" });
  change("location", "kueche/trocken", { lager: "kueche", name: "Trockenlager" });
  change("location", "kueche/kuehlraum", { lager: "kueche", name: "Kühlraum" });

  const items: Item[] = [];
  let n = 0;
  // About 150 wines: vintages of the wine list, in the cellar; the popular ones also at the bar.
  for (let i = 0; i < 150; i++) {
    // 15 wines × 10 vintages, every name once.
    const name = `${WINES[i % WINES.length]} ${2015 + (Math.floor(i / WINES.length) % 10)}`;
    const id = `wein-${++n}`;
    // Cents per ml (4,50 € to 39,50 € a bottle); the bottle is the container of 750 ml.
    const price = (450 + Math.floor(rnd() * 3500)) / BOTTLE;
    change("article", id, {
      name,
      profile: "wein",
      tracking: "lot",
      unit: "ml",
      size: BOTTLE,
      packs: [{ name: "Kiste", plural: "Kisten", factor: 6 * BOTTLE, barcode: ean13(`40${String(n).padStart(6, "0")}9`) }],
      barcodes: [ean13(`40${String(n).padStart(6, "0")}1`)],
      category: i % 3 === 0 ? "Weiß" : "Rot",
    });
    const weight = rnd() < 0.2 ? 6 : 1;
    items.push({ id, lager: "keller", location: pick(cellar), lot: true, step: BOTTLE, refill: 36 * BOTTLE, price, stock: 0, weight, ...(weight > 1 ? { bar: "bar/theke" } : {}) });
  }
  // About 30 spirits at the bar in ml.
  for (let i = 0; i < 30; i++) {
    const id = `spirit-${++n}`;
    change("article", id, {
      name: `${SPIRITS[i % SPIRITS.length]}${i >= SPIRITS.length ? ` ${Math.floor(i / SPIRITS.length) + 1}` : ""}`,
      tracking: "quantity",
      unit: "ml",
      size: 700,
      packs: [{ name: "Flasche", plural: "Flaschen", factor: 700, barcode: ean13(`41${String(n).padStart(6, "0")}1`) }],
      barcodes: [],
    });
    items.push({ id, lager: "bar", location: "bar/theke", lot: false, step: 40, refill: 4200, price: 2 + rnd() * 6, stock: 0, weight: 3 });
  }
  // About 70 foods in the kitchen.
  for (let i = 0; i < 70; i++) {
    const id = `food-${++n}`;
    const lot = i % 4 === 0;
    change("article", id, {
      name: `${FOODS[i % FOODS.length]}${i >= FOODS.length ? ` ${Math.floor(i / FOODS.length) + 1}` : ""}`,
      tracking: lot ? "lot" : "quantity",
      unit: lot ? "Stk." : "g",
      barcodes: [ean13(`42${String(n).padStart(6, "0")}1`)],
    });
    items.push({ id, lager: "kueche", location: i % 2 ? "kueche/trocken" : "kueche/kuehlraum", lot, step: lot ? 1 : 250, refill: lot ? 30 : 10_000, price: lot ? 80 + rnd() * 400 : 0.2 + rnd() * 2, stock: 0, weight: 4 });
  }

  const totalWeight = items.reduce((s, i) => s + i.weight, 0);
  const choose = () => {
    let r = rnd() * totalWeight;
    for (const it of items) if ((r -= it.weight) <= 0) return it;
    return items.at(-1)!;
  };
  const barStock = new Map<string, number>();
  const book = (b: Omit<Booking, "id" | "recordedAt">) => {
    const full = { ...b, id: uuidv7(b.at), recordedAt: b.at } as Booking;
    bookings.push(full);
    return full;
  };
  const receive = (it: Item, at: number) => {
    book({ kind: "in", at, article: it.id, lager: it.lager, location: it.location, qty: it.refill, reason: "delivery", amount: Math.round(it.price * (0.95 + rnd() * 0.1) * it.refill) } as Booking);
    it.stock += it.refill;
  };

  // Opening stock.
  for (const it of items) receive(it, opts.start + 60_000);

  for (let d = 0; d < opts.days; d++) {
    const day = opts.start + d * DAY;
    const open = day + 11 * 3_600_000;
    const span = 12 * 3_600_000;
    const count = Math.round(perDay * (0.7 + rnd() * 0.6));
    for (let i = 0; i < count; i++) {
      const at = open + Math.floor(rnd() * span);
      const it = choose();
      if (it.bar) {
        // Popular wine: sold at the bar, refilled from the cellar.
        const here = barStock.get(it.id) ?? 0;
        if (here < BOTTLE) {
          const qty = Math.min(6 * BOTTLE, Math.max(it.stock, 0));
          if (qty > 0) {
            book({ kind: "move", at: at - 60_000, article: it.id, fromLager: "keller", from: it.location, toLager: "bar", to: it.bar, qty } as Booking);
            it.stock -= qty;
            barStock.set(it.id, here + qty);
          } else {
            receive(it, at - 120_000);
            continue;
          }
        }
        book({ kind: "out", at, article: it.id, lager: "bar", location: it.bar, qty: BOTTLE, reason: rnd() < 0.03 ? "breakage" : "sale" } as Booking);
        barStock.set(it.id, (barStock.get(it.id) ?? 0) - BOTTLE);
        continue;
      }
      if (it.stock < it.step * 3) receive(it, at - 3_600_000);
      const out = book({ kind: "out", at, article: it.id, lager: it.lager, location: it.location, qty: it.step, reason: it.lager === "kueche" ? "kitchen" : "sale" } as Booking);
      it.stock -= it.step;
      // About 1 % are mistakes, cancelled a little later.
      if (rnd() < 0.01) {
        book({ kind: "cancel", at: at + 120_000, cancels: out.id, lagers: [it.lager] } as Booking);
        it.stock += it.step;
      }
    }
    // Monthly inventory on the last day of a 30-day block: every article counted, a little shrinkage.
    if (d % 30 === 29) {
      const at = day + 23 * 3_600_000;
      for (const it of items) {
        const actual = Math.max(0, it.stock - (rnd() < 0.3 ? it.step : 0));
        book({ kind: "count", at, article: it.id, lager: it.lager, location: it.location, qty: actual } as Booking);
        it.stock = actual;
      }
    }
  }
  return { entities, bookings };
}
